"use client";

import HeroSection from "@/components/content/sections/hero";
import ChannelsSection from "@/components/content/sections/channels-section";
import CollectionsSection from "@/components/content/sections/collections-section";
import EpisodesSection from "@/components/content/sections/episodes-section";
import FeaturedSection from "@/components/content/sections/featured-section";
import YouTubeSection from "@/components/content/sections/youtube-section";
import RecentlyWatchedSection from "@/components/content/sections/recently-watched-section";
import FollowedSection from "@/components/content/sections/followed-section";
import { AnalyticsSection } from "@/components/AnalyticsSection";
import { PersonaOnboardingDialog, PersonaOnboardingResult } from "@/components/ui/onboard-dialog";
import { useState, useCallback, useEffect, useMemo } from "react";
import useSWR from 'swr';
import { MobileMenuButton } from "@/components/ui/mobile-menu-button";
import { InstallPrompt } from "@/components/InstallPrompt";
import { useUser } from "@/lib/context/user-context";
import { useUserSettings } from "@/contexts/user-settings-context";
import MicrophoneMeteringExample from "@/components/microphone-metering/example";

// 通用的数据获取函数
const fetcher = (url: string) => fetch(url).then(res => {
  if (!res.ok) throw new Error(`Error: ${res.status}`);
  return res.json();
});

// interface Collection {
//   tag: string;
//   episodes: { shareId: string; avatarUrl: string }[];
// }

interface Episode {
  ownerAvatarUrl: string;
  author: string;
  title: string;
  shareId: string;
  userId?: string;
  videoId?: string;
  isFeaturedEpisode?: boolean;
  duration?: number;
  durationFormatted?: string;
}

interface Podcast {
  ownerAvatarUrl: string;
  author: string;
  name: string;
  slug?: string;
  defaultLevelRange?: string[];
}

const normalizeLevelRange = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(String).map(item => item.trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map(String).map(item => item.trim()).filter(Boolean);
      }
    } catch {
      // fall through to simple string handling
    }

    return value
      .split(/[-,|]/)
      .map(part => part.trim())
      .filter(Boolean);
  }

  return [];
};

export default function Page() {
  // Episodes 分页状态 - 只管理额外加载的episodes
  const [episodesPage, setEpisodesPage] = useState(1);
  const [additionalEpisodes, setAdditionalEpisodes] = useState<Episode[]>([]);
  const [episodesHasMore, setEpisodesHasMore] = useState(true);
  const [isLoadingMoreEpisodes, setIsLoadingMoreEpisodes] = useState(false);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const { user, session } = useUser();
  const {
    personaProfile,
    setUserSettings,
    hasLoaded: hasLoadedUserSettings,
  } = useUserSettings();

  // SWR 配置：这些数据不经常变化，使用激进的缓存策略
  // 全局配置已在 SWRProvider 中设置，这里只覆盖特定配置
  const swrOptions = {
    // 继承全局配置，不需要重复设置
    // revalidateOnFocus, revalidateIfStale 等已在全局配置
  };

  // 使用 SWR 获取集合数据
  const { 
    data: collectionsData, 
    isLoading: isLoadingCollections 
  } = useSWR('/api/trending?type=collections', fetcher, swrOptions);

  // 使用 SWR 获取剧集数据
  const { 
    data: episodesData, 
    isLoading: isLoadingEpisodes 
  } = useSWR('/api/trending?type=episodes&page=1&limit=20', fetcher, swrOptions);

  // 使用 SWR 获取播客数据
  const { 
    data: podcastsData, 
    isLoading: isLoadingPodcasts 
  } = useSWR('/api/trending?type=channels', fetcher, swrOptions);

  // 使用 SWR 获取精选剧集数据
  const { 
    data: featuredData, 
    error: featuredError, 
    isLoading: isLoadingFeatured
  } = useSWR('/api/featured-episodes', fetcher, {
    ...swrOptions,
    fallbackData: { episodes: [] },
  });

  // 更新 hasMore 状态
  useEffect(() => {
    if (episodesData?.pagination?.hasMore !== undefined) {
      setEpisodesHasMore(episodesData.pagination.hasMore);
    }
  }, [episodesData]);

  // 加载更多episodes的函数
  const loadMoreEpisodes = useCallback(async () => {
    if (isLoadingMoreEpisodes || !episodesHasMore) return;
    
    setIsLoadingMoreEpisodes(true);
    try {
      const nextPage = episodesPage + 1;
      const response = await fetcher(`/api/trending?type=episodes&page=${nextPage}&limit=20`);
      
      if (response.episodes && response.episodes.length > 0) {
        setAdditionalEpisodes(prev => [...prev, ...response.episodes]);
        setEpisodesPage(nextPage);
        setEpisodesHasMore(response.pagination?.hasMore || false);
      } else {
        setEpisodesHasMore(false);
      }
    } catch (error) {
      console.error('Failed to load more episodes:', error);
    } finally {
      setIsLoadingMoreEpisodes(false);
    }
  }, [episodesPage, episodesHasMore, isLoadingMoreEpisodes]);

  // 直接使用 SWR 的数据，避免额外的 state 和 effects
  const collections = collectionsData?.collections || [];
  const episodes = [...(episodesData?.episodes || []), ...additionalEpisodes];

  const rawPodcasts = Array.isArray(podcastsData?.data)
    ? podcastsData.data
    : Array.isArray(podcastsData)
      ? podcastsData
      : Array.isArray(podcastsData?.podcasts)
        ? podcastsData.podcasts
        : [];

  const podcasts: Podcast[] = rawPodcasts.map((podcast: any) => ({
    ...podcast,
    slug: podcast?.slug ?? podcast?.id ?? (typeof podcast?.name === "string" ? podcast.name.replace(/\s+/g, "_") : undefined),
    defaultLevelRange: normalizeLevelRange(
      (podcast?.defaultLevelRange ?? podcast?.default_level_range) ?? null
    ),
  }));

  const featuredEpisodes = featuredData?.episodes || [];

  const handlePersonaComplete = useCallback(
    (payload: PersonaOnboardingResult) => {
      setUserSettings((prev) => ({
        ...prev,
        personaProfile: payload,
      }));
      setIsOnboardingOpen(false);
    },
    [setUserSettings],
  );

  useEffect(() => {
    if (!user?.id) {
      setIsOnboardingOpen(false);
      return;
    }
    if (!hasLoadedUserSettings || typeof personaProfile === "undefined") {
      return;
    }
    if (!personaProfile) {
      setIsOnboardingOpen(true);
    } else {
      setIsOnboardingOpen(false);
    }
  }, [user?.id, personaProfile, hasLoadedUserSettings]);

  return (
    <div className="relative edge-to-edge-top edge-to-edge-bottom px-3">
      <InstallPrompt />
      <PersonaOnboardingDialog
        open={isOnboardingOpen}
        onOpenChange={setIsOnboardingOpen}
        onComplete={handlePersonaComplete}
        defaultValues={personaProfile ?? undefined}
      />
      {/* <MobileMenuButton ariaLabel="Open menu" /> */}
      <HeroSection />
      {/* <AnalyticsSection /> */}
      <RecentlyWatchedSection />
      <FollowedSection />
      <YouTubeSection />
      {/* <CollectionsSection
        collections={collections}
        isLoading={isLoadingCollections}
      /> */}
      {/* <FeaturedSection 
        episodes={featuredEpisodes} 
        isLoading={isLoadingFeatured}
        error={featuredError}
      /> */}
      <EpisodesSection 
        episodes={episodes} 
        isLoading={isLoadingEpisodes}
        onLoadMore={loadMoreEpisodes}
        hasMore={episodesHasMore}
        isLoadingMore={isLoadingMoreEpisodes}
      />
      <ChannelsSection 
        podcasts={podcasts} 
        isLoading={isLoadingPodcasts}
      />
    </div>
  );
}
